// React
import React, { useState, useEffect, useRef } from 'react';

// Packages
import cc from 'classcat';
import t from 'prop-types';
import { useController } from 'react-hook-form';

// Utilities

// Components

// Icons
import { FiCircle, FiCheckCircle, FiX } from 'react-icons/fi';

const SearchFilterMultiselectComponent = ({
    controller,
    label,
    name,
    options,
}) => {
    // ///////////////////
    // HOOKS
    // ///////////////////

    // Controller from useForm
    const {
        field: { onChange, value },
    } = useController({
        name,
        defaultValue: [],
        control: controller,
    });

    // ///////////////////
    // STATE
    // ///////////////////

    const [listWrapper, toggleListWrapper] = useState(false);

    // ///////////////////
    // REFS
    // ///////////////////

    const listWrapperRef = useRef(null);

    // ///////////////////
    // METHODS
    // ///////////////////

    function handleClick(event) {
        if (
            listWrapper &&
            listWrapperRef.current &&
            !listWrapperRef.current.contains(event.target)
        ) {
            toggleListWrapper(false);
        }
    }

    function toggleItemFromValues(item) {
        // Take into account that item.value can be an array
        // Check if item.value is an array
        if (Array.isArray(item.value)) {
            const itemExists = item.value.some(valueItem =>
                value.includes(valueItem)
            );
            // Remove
            if (itemExists) {
                return [
                    ...value.filter(valueItem =>
                        item.value.some(itemValue => itemValue !== valueItem)
                    ),
                ];
            }
            // Add
            return [...value, ...item.value];
        }

        // Check for item in list
        const itemExists = value.includes(item.value);

        // Remove
        if (itemExists) {
            return [...value.filter(valueItem => item.value !== valueItem)];
        }

        // Add
        return [...value, item.value];
    }

    function showValueLabel(value) {
        return value
            .map(v => {
                // Get option label
                return options.find(option => {
                    // Check if option values value is an array
                    if (Array.isArray(option.value)) {
                        // Check if value is in array
                        return option.value.some(optionValue =>
                            v.includes(optionValue)
                        );
                    }
                    // Else check if value is equal
                    return option.value === v;
                })?.label;
            })
            .join(', ');
    }

    function isSelected(value, option) {
        // Check if option values value is an array
        if (Array.isArray(option.value)) {
            // Check if value is in array
            return option.value.some(optionValue => {
                return value.flat().includes(optionValue);
            });
        }
        // Else check if value is equal
        return value.includes(option.value);
    }

    // ///////////////////
    // EFFECTS
    // ///////////////////

    // Effect: Catch outside clicks and close
    useEffect(() => {
        document.addEventListener('click', handleClick, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, [listWrapper]);

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <div className="relative flex m-8">
            <button
                className={cc([
                    'py-6 text-left text-blue-300 outline-none pt-11 focus:ring-2 bg-blue-20 t-small focus:ring-blue-100 focus:outline-none',
                    {
                        'rounded-l-4 pr-8 pl-16': value.length > 0,
                        'rounded-4 px-16': value.length === 0,
                    },
                ])}
                onClick={event => {
                    event.preventDefault();
                    toggleListWrapper(!listWrapper);
                }}>
                {`${label} `}
                <span className="font-bold">
                    <span className="font-bold">
                        {value.length > 0 && value.length < 2
                            ? showValueLabel(value)
                            : '...'}
                        {value.length > 1 && ` (${value.length})`}
                    </span>
                </span>
            </button>
            {value.length > 0 && (
                <button
                    onClick={() => onChange([])}
                    className="pl-8 pr-16 text-blue-300 outline-none focus:ring-2 bg-blue-20 t-small focus:ring-blue-100 focus:outline-none rounded-r-4">
                    <FiX />
                </button>
            )}
            <div
                ref={listWrapperRef}
                className={cc([
                    'absolute flex flex-col p-16 pr-64 mt-8 space-y-16 text-blue-300 t-h6 bg-blue-20 top-full rounded-4 z-above transition-default',
                    {
                        'opacity-100 pointer-events-auto': listWrapper,
                        'opacity-0 pointer-events-none': !listWrapper,
                    },
                ])}>
                {options
                    .filter(option => !option.disabledInSelect)
                    .map(option => (
                        <button
                            key={option.value}
                            onClick={event => {
                                event.preventDefault();
                                // Get new values list
                                const newList = toggleItemFromValues(option);
                                onChange(newList);
                            }}
                            className={cc([
                                'flex whitespace-nowrap items-center space-x-8 text-left outline-none focus:outline-none',
                                {
                                    'text-blue-100': isSelected(value, option),
                                },
                            ])}>
                            {isSelected(value, option) ? (
                                <FiCheckCircle className="pointer-events-none" />
                            ) : (
                                <FiCircle className="pointer-events-none" />
                            )}
                            <span className="mt-4">{option.label}</span>
                        </button>
                    ))}
            </div>
        </div>
    );
};

SearchFilterMultiselectComponent.propTypes = {
    name: t.string,
    options: t.arrayOf(
        t.shape({
            label: t.string,
            value: t.oneOfType([t.string, t.number, t.bool]),
        })
    ),
};

SearchFilterMultiselectComponent.defaultProps = {
    options: [],
};

export default SearchFilterMultiselectComponent;
